import React, { useState, useRef } from 'react'
import { compose } from 'redux'
import { Provider } from 'react-redux'
import { ConnectedRouter } from 'connected-react-router'
import { withLDProvider } from 'launchdarkly-react-client-sdk'

import AppContainer from './common/layout/AppContainer'
import { fetchCrossBrowserCSS, isBadBrowser, renderBadBrowserMessage } from './common/crossBrowser'
import { privateLDAttributeNames, defaultAnonymousUser } from '../helpers/ldHelpers'
import AuthProvider from './auth/auth-provider'
import { authConfigEndpoints } from './auth/auth-client.http'

fetchCrossBrowserCSS()

// Main provider component that doesn't use Redux hooks
const AppProvider = ({ store, history }) => {
  const initialized = useRef()
  const [initializedConfig, setInitializedConfig] = useState(false)
  const [authConfig, setAuthConfig] = useState()

  if (!initialized.current) {
    initialized.current = true
    const getConfig = async () => {
      try {
        // Fetch auth configuration from the specified endpoint
        const response = await authConfigEndpoints.GET_CONFIG()
        setAuthConfig({
          use_identity_login: response?.use_identity_login || true,
          use_frame: response?.use_frame || false,
        })
      } catch (error) {
        console.error('Error fetching auth configuration:', error)
        setAuthConfig({
          use_identity_login: true,
          use_frame: false,
        })
      } finally {
        setInitializedConfig(true)
      }
    }
    getConfig()
  }

  // Handle browser compatibility - can be done outside Redux
  if (isBadBrowser) {
    return renderBadBrowserMessage
  }


  if (!initialized.current || !initializedConfig || authConfig === undefined) {
    return null
  }

  return (
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <AuthProvider authConfig={authConfig}>
          <AppContainer />
        </AuthProvider>
      </ConnectedRouter>
    </Provider>
  )
}

// The enhanced component with all the HOCs
const EnhancedAppProvider = compose(
  withLDProvider({
    clientSideID: LD_CLIENT_ID,
    user: defaultAnonymousUser,
    options: { privateAttributeNames: privateLDAttributeNames },
  }),
)(AppProvider)

// Export the enhanced component (Vite handles HMR automatically)
export default EnhancedAppProvider
